import { IInstallmentScheduleObject, IRecalculatePreOfferParams } from 'service/PreOfferService/PreOfferServiceProps'
import { MINIMAL_GAP_BETWEEN_SELECTED_MONTHS } from './InstalmentsMonthsSlice'
import { IInstalmentsMonthsState } from './InstalmentsMonthsSliceModels'
import { PaymentSchedule } from 'models/prepare.offert.model.dto'


/**
 * Funkcja pomocnicza tworząca tablicę tablic. Każda tablica to odcinek miesięcy 
 * NIEZAZNACZONYCH (isSelected=false) i Z BŁĘDEM WALIDACYJNYM(hasError=true). 
 * W momencie gdy naptokamy ZAZNACZONY miesiąc, tworzymy kolejną tablicę z kolejnym ciągiem miesięcy z BŁĘDEM.
 * 
 * PRZYKŁADOWY WYNIK DZIAŁANIA FUNKCJI: [[{label: 'lut'...}, {label: 'mar'...}], [{label: 'cze'...}, {label: 'lip'...}, {label: 'sie'...}, {label: 'wrz'...}]] 
 * Oznacza to że Luty oraz Marzec mają hasError: true; oraz isSelected: false, następnie Kwiecień oraz Maj mają isSelected: true; hasError: false, itd.
 * 
 * @param currentInstalmentsMonthsState 
 * @returns 
 */
const  chunkArrayWithCondition = (currentInstalmentsMonthsState: IInstalmentsMonthsState[]) =>{
   const chunks: IInstalmentsMonthsState[][] = []
   let currentChunk: IInstalmentsMonthsState[] = []

   currentInstalmentsMonthsState.forEach((instalmentSingleMonth: IInstalmentsMonthsState) => {
       if (!instalmentSingleMonth.isSelected && instalmentSingleMonth.hasError) {
           currentChunk.push(instalmentSingleMonth)
       } 
       else {
         if (currentChunk.length > 0) {
            chunks.push(currentChunk)
            currentChunk = []
         }
       }
   })

   if (currentChunk.length > 0) {
       chunks.push(currentChunk)
   }

   return chunks
}


/**
 * Funkcja pomocnicza - zwraca tablicę z indeksami ostatnich niezaznaczonych bez przerw miesięcy z rzędu.
*/
export const getLastNotSelectedMonth = (currentInstalmentsMonthsState: IInstalmentsMonthsState[] | PaymentSchedule[]): number[] => {
   const lastNotSelectedMonthsInstallmentNumbers = []
   
   for(let j = currentInstalmentsMonthsState.length - 1; j >= 0; j--) { // Pętla od końca tablicy z miesiącami do początku. 
      if(!currentInstalmentsMonthsState[j].isSelected) {
         lastNotSelectedMonthsInstallmentNumbers.push(currentInstalmentsMonthsState[j].installmentNumber) // Tworzymy tablicę z miesiącami od końca które nie są zaznaczone
      }
      else {
         break;
      }
   }

   return lastNotSelectedMonthsInstallmentNumbers
}


/**
 * Funkcja działająca w momencie gdy użytkownik zaznacza dany miesiąc. 
 * Sprawdzamy czy rozmiar którejś z tablic z funkcji chunkArrayWithCondition jest większy  niż MINIMAL_GAP_BETWEEN_SELECTED_MONTH.
 * Jeśli tak, oznacza to że "przerwa" pomiędzy zaznaczonymi miesiącami jest ZBYT DUŻA i nie usuwamy błędu walidacyjnego hasError.
 * Jeśli "przerwa" jest mniejsza niż zadana w wymaganiach projektu, usuwamy błędy walidacyjne w danych miesiącach, ponieważ spełniają już one wymagania.
 * @param currentInstalmentsMonthsState 
 */
export const cleanInstalmentsMonthErrorOnSelect = (currentInstalmentsMonthsState: IInstalmentsMonthsState[]) => {
   const lastNotSelectedMonthsInstallmentNumbers = getLastNotSelectedMonth(currentInstalmentsMonthsState) // Pobieramy tablice indeksów ostatnich odznaczonych miesięcy

    // Na podstawie pobranej tablicy niezaznaczonych z rzędu ostatnich miesięcy, zaznaczamy hasError na true, dla wszystkich niezaznaczonych pozostałych miesięcy. 
   for (let i = 0; i <= currentInstalmentsMonthsState.length - 1; i++) { 
      if(!lastNotSelectedMonthsInstallmentNumbers.includes(currentInstalmentsMonthsState[i].installmentNumber) && !currentInstalmentsMonthsState[i].isSelected){
         currentInstalmentsMonthsState[i].hasError = true
      }
   }

   const allInstalmentsWithErrorArray = chunkArrayWithCondition(currentInstalmentsMonthsState)
   
   allInstalmentsWithErrorArray.forEach((instalmentMonthsWithErrorArray: IInstalmentsMonthsState[]) => {
      if(instalmentMonthsWithErrorArray.length <= MINIMAL_GAP_BETWEEN_SELECTED_MONTHS) {
         for (const instalmentSingleMonth of instalmentMonthsWithErrorArray) {
            instalmentSingleMonth.hasError = false // W tym przypadku błąd jest usuwany - miesiące spełniają założenia projektu i zostały poprawnie zaznaczone
         }
      }
   })
}

export const setInstalmentsMonthErrorOnUnselect = (currentInstalmentsMonthsState: IInstalmentsMonthsState[]) => {
   let gapBetweenSelectedElementsSize = 0

   for (const month of currentInstalmentsMonthsState) {
       if (!month.isSelected) {

         gapBetweenSelectedElementsSize++

         if (gapBetweenSelectedElementsSize >= MINIMAL_GAP_BETWEEN_SELECTED_MONTHS + 1) {
            const startIndex = currentInstalmentsMonthsState.indexOf(month) - gapBetweenSelectedElementsSize + 1 //Index PIERWSZEGO błędnie zaznaczonego miesiąca
            const endIndex = currentInstalmentsMonthsState.indexOf(month) //Index OSTATNIEGO błędnie zaznaczonego miesiąca

            const lastNotSelectedMonthsInstallmentNumbers = getLastNotSelectedMonth(currentInstalmentsMonthsState)

            for (let i = startIndex; i <= endIndex; i++) {
               if(!lastNotSelectedMonthsInstallmentNumbers.includes(currentInstalmentsMonthsState[i].installmentNumber)){
                  currentInstalmentsMonthsState[i].hasError = true // Ustawiamy dla wszystkich BŁĘDNIE ZAZNACZONYCH miesięcy
               }
               else {
                  currentInstalmentsMonthsState[i].hasError = false
               }
            }
         }
       } 
       else {
         gapBetweenSelectedElementsSize = 0 //Gdy napotkamy PRAWIDŁOWO ZAZNACZONY miesiąc, zerujemy zmienną
       }
   }
}

/**
 * Funkcja mapująca aktualny state kalendarza z wyborem miesięcy na model używany w requeście do z przeliczeniem oferty z backendu.
 * @param currentInstalmentsMonthsState 
 * @returns 
 */
export const mapCurrentMonthStateToPreOffertModel = (currentInstalmentsMonthsState: IInstalmentsMonthsState[]): IRecalculatePreOfferParams => {
   if(currentInstalmentsMonthsState.length === 12){
      const instalmentScheduleArray: IInstallmentScheduleObject[] = currentInstalmentsMonthsState.map((currentInstalmentMonth: IInstalmentsMonthsState) => {
         return {
            isSelected: currentInstalmentMonth.isSelected,
            installmentNumber: currentInstalmentMonth.installmentNumber
         }
      })

      return {
         installmentSchedule: instalmentScheduleArray
      }
   }
   else {
      return {
         installmentSchedule: []
      }
   }
}